import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

// ─────────────────────────────────────────────────────────────────────────────
// Clarity by Avista — landing page
//
// Designed to feel like a high-end product page (Linear / Pastel / Vercel
// vibe) while staying lightweight: no images, no animation libraries, no
// extra dependencies. Every visual is composed HTML/CSS so it scales crisp
// at any density and stays consistent with the dashboard's own design
// tokens (Tailwind neutrals + the brand pin green #1AE07D / orange #E05505).
//
// Section flow:
//   1. Hero                  — wordmark eyebrow + headline + sub + dual CTA
//   2. BrowserMock           — composed product visual under the hero
//   3. How it works          — three workflow steps with mini-illustrations
//   4. CascadingFeatures     — three big 01/02/03 blocks, alternating sides
//   5. ProductShowcase       — tabbed UI deep-dive
//   6. Built for Avista      — dark rhythm-break with card stack
//   7. Closing CTA           — repeat of the primary action
//   8. Footer                — wordmark + small links + Avista credit
// ─────────────────────────────────────────────────────────────────────────────

export function LandingPage() {
  const { state } = useAuth();
  const authed = state.status === "authed";
  const primaryHref = authed ? "/dashboard" : "/login";
  const primaryLabel = authed ? "Go to dashboard" : "Log in";

  return (
    <>
      <LandingStyles />
      <Hero primaryHref={primaryHref} primaryLabel={primaryLabel} />
      <HowItWorks />
      <CascadingFeatures />
      <ProductShowcase />
      <BuiltForAvista />
      <ClosingCta primaryHref={primaryHref} primaryLabel={primaryLabel} />
      <SiteFooter />
    </>
  );
}

// ── Landing-only animations ────────────────────────────────────────────────
//
// Inline <style> so the landing's motion vocabulary doesn't leak into the
// dashboard bundle's CSS. Keyframes used:
//   - clarity-reveal-up: opacity + translate, driven by IntersectionObserver
//   - clarity-fade-in:   opacity-only, used for the showcase tab swap
//   - clarity-live-pulse: soft expanding ring on the hero pin / live dot
// All animations sit behind `prefers-reduced-motion: reduce` — users who
// asked the OS for less motion just get the static end-state.

function LandingStyles() {
  return (
    <style>{`
      @keyframes clarity-reveal-up {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes clarity-fade-in {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes clarity-live-pulse {
        0%   { box-shadow: 0 0 0 0 rgba(26, 224, 125, 0.55); }
        70%  { box-shadow: 0 0 0 10px rgba(26, 224, 125, 0); }
        100% { box-shadow: 0 0 0 0 rgba(26, 224, 125, 0); }
      }
      .clarity-reveal-hidden { opacity: 0; transform: translateY(16px); }
      .clarity-reveal-show   { animation: clarity-reveal-up 0.7s ease-out forwards; }
      .clarity-fade-in       { animation: clarity-fade-in 0.32s ease-out; }
      .clarity-live-pulse    { animation: clarity-live-pulse 1.8s ease-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .clarity-reveal-hidden { opacity: 1; transform: none; }
        .clarity-reveal-show   { animation: none; }
        .clarity-fade-in       { animation: none; }
        .clarity-live-pulse    { animation: none; }
      }
    `}</style>
  );
}

// Faint dot grid backdrop, masked with a radial fade so it doesn't read as
// graph paper. Lives behind content (-z-10) and pointer-events: none so
// clicks pass straight through. Bring this into any section you want to
// feel "designed surface" — keep it out of sections that should stay
// quiet (dark Avista block, footer, etc.).
function DotGrid({ className = "" }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={
        "pointer-events-none absolute inset-0 -z-10 " + className
      }
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(15,15,15,0.07) 1px, transparent 0)",
        backgroundSize: "24px 24px",
        WebkitMaskImage:
          "radial-gradient(ellipse 80% 70% at 50% 40%, black 30%, transparent 80%)",
        maskImage:
          "radial-gradient(ellipse 80% 70% at 50% 40%, black 30%, transparent 80%)",
      }}
    />
  );
}

// Wraps any child in a scroll-triggered reveal. Uses IntersectionObserver
// so off-screen content stays unpainted-ish until it enters the viewport.
// `delay` lets callers stagger neighbours (eyebrow / headline / sub / CTAs).
// Caller stays unaware of motion preferences — the CSS handles that.
function RevealOnScroll({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // If IntersectionObserver isn't available (rare these days, but cheap
    // to guard), fail-open by showing the content immediately.
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setRevealed(true);
            obs.disconnect();
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={
        (revealed ? "clarity-reveal-show" : "clarity-reveal-hidden") +
        " " +
        className
      }
      style={{ animationDelay: revealed ? `${delay}ms` : undefined }}
    >
      {children}
    </div>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────

function Hero({
  primaryHref,
  primaryLabel,
}: {
  primaryHref: string;
  primaryLabel: string;
}) {
  return (
    <section className="relative overflow-hidden bg-white">
      <DotGrid />
      <div className="relative mx-auto max-w-6xl px-6 pb-12 pt-20 sm:pt-28 md:pb-20">
        <RevealOnScroll delay={0}>
          <p className="text-center text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
            Clarity by Avista
          </p>
        </RevealOnScroll>
        <RevealOnScroll delay={80}>
          <h1 className="mx-auto mt-5 max-w-4xl text-center text-5xl font-semibold leading-[1.04] tracking-[-0.02em] text-neutral-900 sm:text-6xl md:text-[80px]">
            Point-and-click feedback on any website.
          </h1>
        </RevealOnScroll>
        <RevealOnScroll delay={160}>
          <p className="mx-auto mt-7 max-w-2xl text-center text-lg leading-relaxed text-neutral-600 md:text-xl">
            Built for Avista clients to review work in progress — no plugins, no
            logins for reviewers.
          </p>
        </RevealOnScroll>
        <RevealOnScroll delay={240}>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Link
              to={primaryHref}
              className="rounded-md bg-neutral-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-neutral-700"
            >
              {primaryLabel}
            </Link>
            <a
              href="#features"
              className="rounded-md border border-neutral-300 px-5 py-3 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100"
            >
              See how it works
            </a>
          </div>
        </RevealOnScroll>
      </div>

      <div className="relative mx-auto max-w-6xl px-6 pb-24">
        <RevealOnScroll delay={320}>
          <BrowserMock />
        </RevealOnScroll>
      </div>

      {/* Soft backdrop wash gives the visual a sense of depth without a
          gradient blob cliché. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-1/3 -z-20 mx-auto h-[60vh] max-w-5xl rounded-full bg-gradient-to-tr from-neutral-200/50 via-neutral-100/40 to-transparent blur-3xl"
      />
    </section>
  );
}

// ── Browser mock for the hero ──────────────────────────────────────────────

function BrowserMock() {
  return (
    <div className="relative mx-auto max-w-5xl">
      <div
        aria-hidden
        className="absolute -inset-x-4 -inset-y-2 -z-10 rounded-[28px] bg-gradient-to-b from-neutral-200/70 to-neutral-100/30 blur-2xl"
      />

      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,15,15,0.25),0_8px_30px_-12px_rgba(15,15,15,0.12)]">
        {/* Top chrome */}
        <div className="flex items-center gap-3 border-b border-neutral-200 bg-neutral-50/80 px-4 py-3">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
          </div>
          <div className="ml-2 flex flex-1 items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-500">
            <LockIcon />
            <span className="font-mono">clients.avista.is/proposal-v3</span>
          </div>
          <div className="hidden gap-1.5 sm:flex">
            <span className="h-5 w-5 rounded bg-neutral-200" />
            <span className="h-5 w-5 rounded bg-neutral-200" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr,280px]">
          {/* Mock site canvas */}
          <div className="relative h-[420px] overflow-hidden bg-white sm:h-[480px]">
            <MockSite />

            <Pin x="22%" y="20%" label="DH" />
            <Pin x="68%" y="46%" label="HE" live />
            <Pin x="40%" y="78%" label="DH" tone="resolved" />

            <FloatingComment x="44%" y="52%" />
          </div>

          {/* Right rail: comments sidebar */}
          <aside className="hidden border-l border-neutral-200 bg-white md:block">
            <div className="border-b border-neutral-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                  Comments
                </p>
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-neutral-600">
                  <span
                    aria-hidden
                    className="clarity-live-pulse inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: "#1AE07D" }}
                  />
                  Live
                </span>
              </div>
              <div className="mt-2 inline-flex rounded-md bg-neutral-100 p-0.5 text-[10px]">
                <span className="rounded bg-white px-2 py-0.5 text-neutral-900 shadow-sm">
                  Open · 2
                </span>
                <span className="px-2 py-0.5 text-neutral-500">Resolved · 1</span>
              </div>
            </div>

            <div className="divide-y divide-neutral-100">
              <MockComment
                initials="DH"
                name="David H."
                body="Could the hero headline be a touch larger on mobile?"
              />
              <MockComment
                initials="HE"
                name="Helena E."
                body="Love the new CTA copy. Maybe try a green hover state?"
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function MockSite() {
  return (
    <div className="p-8 sm:p-10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-5 w-5 rounded-md bg-neutral-900" />
          <span className="h-2 w-16 rounded bg-neutral-300" />
        </div>
        <div className="flex items-center gap-5">
          <span className="h-2 w-10 rounded bg-neutral-200" />
          <span className="h-2 w-12 rounded bg-neutral-200" />
          <span className="h-2 w-8 rounded bg-neutral-200" />
          <span className="h-6 w-16 rounded-md bg-neutral-900" />
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-8 sm:mt-14 sm:grid-cols-2">
        <div>
          <div className="h-3 w-24 rounded bg-neutral-200" />
          <div className="mt-5 space-y-3">
            <div className="h-7 w-full rounded bg-neutral-300" />
            <div className="h-7 w-11/12 rounded bg-neutral-300" />
            <div className="h-7 w-2/3 rounded bg-neutral-300" />
          </div>
          <div className="mt-6 space-y-2">
            <div className="h-2 w-full rounded bg-neutral-200" />
            <div className="h-2 w-5/6 rounded bg-neutral-200" />
            <div className="h-2 w-4/6 rounded bg-neutral-200" />
          </div>
          <div className="mt-7 flex gap-3">
            <span className="h-8 w-28 rounded-md bg-neutral-900" />
            <span className="h-8 w-24 rounded-md border border-neutral-300" />
          </div>
        </div>
        <div className="relative h-44 overflow-hidden rounded-xl bg-gradient-to-br from-neutral-100 to-neutral-200 sm:h-56">
          <div className="absolute inset-0 [background:repeating-linear-gradient(45deg,transparent_0_24px,rgba(0,0,0,0.03)_24px_25px)]" />
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
            <span className="h-2 w-12 rounded bg-white/80" />
            <span className="h-2 w-8 rounded bg-white/60" />
          </div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-neutral-200 p-4">
            <span className="block h-8 w-8 rounded-md bg-neutral-100" />
            <div className="mt-3 h-2 w-3/4 rounded bg-neutral-200" />
            <div className="mt-2 space-y-1.5">
              <div className="h-1.5 w-full rounded bg-neutral-100" />
              <div className="h-1.5 w-5/6 rounded bg-neutral-100" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── How it works ───────────────────────────────────────────────────────────

function HowItWorks() {
  return (
    <section id="how-it-works" className="border-t border-neutral-200 bg-neutral-50/60">
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-32">
        <SectionEyebrow>How it works</SectionEyebrow>
        <h2 className="mt-4 max-w-3xl text-3xl font-semibold leading-tight tracking-tight text-neutral-900 md:text-5xl">
          Three steps from URL to actionable feedback.
        </h2>
        <p className="mt-5 max-w-2xl text-lg text-neutral-600">
          No plugins. No site changes. Reviewers don&apos;t make accounts.
        </p>

        <div className="mt-16 grid gap-8 md:grid-cols-3 md:gap-10">
          <Step
            number="01"
            title="Paste the URL"
            body="Drop the page you want feedback on into a new project. Multi-page sites get a tabbed sidebar — list pages by hand or auto-discover from the live site."
            illustration={<StepCreate />}
          />
          <Step
            number="02"
            title="Share the link"
            body="Each project gets a unique share link. Send it to a client, a teammate, or a contractor. They open it, see the live page, and start clicking."
            illustration={<StepShare />}
          />
          <Step
            number="03"
            title="Collect pinned feedback"
            body="Reviewers click anywhere on the page to leave a comment. You see pins in real time, get email notifications, reply, and resolve when done."
            illustration={<StepFeedback />}
          />
        </div>
      </div>
    </section>
  );
}

function Step({
  number,
  title,
  body,
  illustration,
}: {
  number: string;
  title: string;
  body: string;
  illustration: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="relative h-44 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_10px_30px_-15px_rgba(15,15,15,0.15)]">
        {illustration}
      </div>
      <div className="mt-6 flex items-baseline gap-3">
        <span className="text-xs font-semibold tracking-widest text-neutral-400">
          {number}
        </span>
        <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600">{body}</p>
    </div>
  );
}

// Mini-illustrations for each step — same design language as the hero mock.

function StepCreate() {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="w-full max-w-[280px] rounded-xl border border-neutral-200 bg-white p-4 shadow-md">
        <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
          New project
        </p>
        <div className="mt-3 space-y-2">
          <div className="rounded-md border border-neutral-200 px-2 py-1.5 text-[10px] text-neutral-500">
            Marketing homepage v3
          </div>
          <div className="rounded-md border border-neutral-200 px-2 py-1.5 text-[10px] text-neutral-500">
            https://clients.avista.is/proposal-v3
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <span className="rounded-md bg-neutral-900 px-2.5 py-1 text-[10px] font-medium text-white">
            Create project
          </span>
        </div>
      </div>
    </div>
  );
}

function StepShare() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6">
      <div className="flex w-full max-w-[280px] items-center gap-2 rounded-md border border-neutral-200 bg-white px-2 py-1.5">
        <LinkIcon />
        <span className="flex-1 truncate font-mono text-[10px] text-neutral-500">
          clarity.avista.is/p/Qa8…7Rv
        </span>
        <span className="rounded bg-neutral-900 px-2 py-0.5 text-[10px] text-white">
          Copy
        </span>
      </div>
      <div className="flex items-center gap-2 text-[10px] text-neutral-500">
        <ArrowDownIcon />
        <span>Send to client</span>
      </div>
      <div className="flex gap-1.5">
        <Avatar mark="DH" />
        <Avatar mark="HE" tone="brand" />
        <Avatar mark="JP" />
      </div>
    </div>
  );
}

function StepFeedback() {
  return (
    <div className="absolute inset-0">
      {/* Faded page underneath */}
      <div className="absolute inset-0 m-3 rounded-lg bg-gradient-to-br from-neutral-50 to-neutral-100 opacity-80">
        <div className="px-3 pt-3">
          <div className="h-2 w-16 rounded bg-neutral-200" />
          <div className="mt-2 h-3 w-3/4 rounded bg-neutral-200" />
          <div className="mt-1 h-3 w-1/2 rounded bg-neutral-200" />
        </div>
      </div>
      {/* Pin */}
      <div className="absolute left-[28%] top-[42%]">
        <PinShape label="DH" tone="open" />
      </div>
      {/* Floating mini-comment */}
      <div className="absolute right-3 top-3 w-[140px] rounded-md border border-neutral-200 bg-white p-2 shadow-md">
        <div className="flex items-center gap-1.5">
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-neutral-900 text-[8px] font-semibold text-white">
            DH
          </span>
          <span className="text-[9px] font-medium text-neutral-900">David H.</span>
        </div>
        <p className="mt-1 text-[9px] leading-snug text-neutral-700">
          Tighten the line-height here.
        </p>
      </div>
    </div>
  );
}

// ── Cascading feature blocks ───────────────────────────────────────────────
//
// Three big numbered blocks (01 / 02 / 03), alternating sides. Each block
// gets a sizeable product visual paired with a single, focused claim —
// the signature ideas that distinguish Clarity. Smaller features (viewport
// previews, threading, notifications, hide-pages) live in ProductShowcase
// below so the cascading section stays cinematic instead of encyclopedic.

function CascadingFeatures() {
  return (
    <section id="features" className="relative overflow-hidden bg-white">
      <DotGrid />
      <div className="relative mx-auto max-w-6xl px-6 py-24 md:py-32">
        <RevealOnScroll>
          <SectionEyebrow>What&apos;s inside</SectionEyebrow>
        </RevealOnScroll>
        <RevealOnScroll delay={80}>
          <h2 className="mt-4 max-w-3xl text-3xl font-semibold leading-tight tracking-tight text-neutral-900 md:text-5xl">
            Three moves that make the review actually useful.
          </h2>
        </RevealOnScroll>
        <RevealOnScroll delay={160}>
          <p className="mt-5 max-w-2xl text-lg text-neutral-600">
            Each one solves a sharp paper-cut in the typical
            “send-a-screenshot, get-vague-feedback” loop.
          </p>
        </RevealOnScroll>

        <div className="mt-20 space-y-24 md:mt-24 md:space-y-32">
          <RevealOnScroll>
            <CascadeBlock
              number="01"
              title="Pin feedback anywhere on the live page"
              body="Reviewers click any spot on your real site — a heading, a button, the gap between two cards. The pin anchors to the element underneath and survives reflow, so the note stays where it was meant to be when the page repaints."
              visual={<VisualPin />}
              align="right"
            />
          </RevealOnScroll>
          <RevealOnScroll>
            <CascadeBlock
              number="02"
              title="Multi-page projects, auto-discovered"
              body="Paste a URL once. Clarity crawls the site and lays out every page in a sidebar with its open-comment count. Owners rename or hide pages from the collaborator view without touching a thing in the source."
              visual={<VisualPages />}
              align="left"
            />
          </RevealOnScroll>
          <RevealOnScroll>
            <CascadeBlock
              number="03"
              title="Reviewer-friendly: no signup, no plugins"
              body="Send the share link. Reviewers open it, see your live page, and start clicking. Name and email are captured on the first comment and cached on the device — they never face an account screen, and you never chase logins."
              visual={<VisualShare />}
              align="right"
            />
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}

function CascadeBlock({
  number,
  title,
  body,
  visual,
  align,
}: {
  number: string;
  title: string;
  body: string;
  visual: React.ReactNode;
  align: "left" | "right";
}) {
  const proseFirst = align === "left";
  return (
    <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
      <div className={proseFirst ? "md:order-1" : "md:order-2"}>
        <p className="text-xs font-semibold tracking-[0.3em] text-neutral-400">
          {number}
        </p>
        <h3 className="mt-3 text-2xl font-semibold leading-tight tracking-tight text-neutral-900 md:text-4xl">
          {title}
        </h3>
        <p className="mt-4 max-w-lg text-base leading-relaxed text-neutral-600 md:text-lg">
          {body}
        </p>
      </div>
      <div className={proseFirst ? "md:order-2" : "md:order-1"}>
        <div className="relative">
          <div
            aria-hidden
            className="absolute -inset-x-3 -inset-y-2 -z-10 rounded-[28px] bg-gradient-to-b from-neutral-200/70 to-transparent blur-2xl"
          />
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,15,15,0.18),0_8px_30px_-12px_rgba(15,15,15,0.08)]">
            {visual}
          </div>
        </div>
      </div>
    </div>
  );
}

// Cascading visual #1 — close-up on a pin anchored to a heading, with the
// underlying element subtly highlighted to show the anchoring claim.
function VisualPin() {
  return (
    <div className="relative h-[340px] bg-neutral-50 sm:h-[400px]">
      <div className="absolute inset-0 p-8 sm:p-12">
        <div className="space-y-3">
          <div className="h-2.5 w-20 rounded bg-neutral-200" />
          <div className="relative inline-block max-w-full">
            <div className="space-y-2">
              <div className="h-6 w-72 rounded bg-neutral-300 sm:w-96" />
              <div className="h-6 w-56 rounded bg-neutral-300 sm:w-72" />
            </div>
            {/* element highlight */}
            <div
              aria-hidden
              className="absolute -inset-2 rounded-md ring-2"
              style={{ boxShadow: "0 0 0 2px #1AE07D inset" }}
            />
          </div>
          <div className="space-y-1.5 pt-4">
            <div className="h-2 w-full max-w-md rounded bg-neutral-200" />
            <div className="h-2 w-5/6 max-w-md rounded bg-neutral-200" />
            <div className="h-2 w-4/6 max-w-md rounded bg-neutral-200" />
          </div>
        </div>
        {/* the pin itself */}
        <div className="absolute right-12 top-8">
          <div className="relative">
            <div
              className="inline-flex h-9 min-w-[36px] items-center justify-center border-2 border-white px-2 text-xs font-semibold leading-none shadow-[0_8px_24px_rgba(15,15,15,0.25)]"
              style={{
                background: "#1AE07D",
                color: "#0a2a17",
                borderRadius: "9999px 9999px 9999px 2px",
              }}
            >
              DH
            </div>
            <div className="absolute right-0 mt-3 w-[220px] rounded-xl border border-neutral-200 bg-white p-3 text-left shadow-[0_18px_44px_-12px_rgba(15,15,15,0.2)]">
              <p className="text-[11px] font-medium text-neutral-900">
                David H. <span className="font-normal text-neutral-400">· just now</span>
              </p>
              <p className="mt-0.5 text-[11px] leading-snug text-neutral-700">
                Headline could be one line tighter on mobile.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cascading visual #2 — a multi-page sidebar with auto-discovery in flight.
function VisualPages() {
  return (
    <div className="grid h-[340px] grid-cols-[200px,1fr] sm:h-[400px]">
      <div className="flex flex-col border-r border-neutral-200 bg-neutral-50 px-4 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Pages
        </p>
        <ul className="mt-3 space-y-1 text-xs">
          <li className="flex items-center justify-between rounded-md bg-neutral-900 px-2 py-1.5 text-white">
            <span>Home</span>
            <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold">
              3
            </span>
          </li>
          <li className="flex items-center justify-between rounded-md px-2 py-1.5 text-neutral-700">
            <span>About</span>
            <span className="rounded-full bg-neutral-200 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-700">
              1
            </span>
          </li>
          <li className="flex items-center justify-between rounded-md px-2 py-1.5 text-neutral-700">
            <span>Services</span>
          </li>
          <li className="flex items-center justify-between rounded-md px-2 py-1.5 italic text-neutral-400">
            <span>Pricing</span>
            <span className="inline-flex items-center text-neutral-400">
              <EyeOffSvg />
            </span>
          </li>
          <li className="flex items-center justify-between rounded-md px-2 py-1.5 text-neutral-700">
            <span>Contact</span>
          </li>
        </ul>
        <div className="mt-3 flex items-center gap-2 rounded-md border border-dashed border-neutral-300 px-2 py-1.5 text-[10px] text-neutral-500">
          <SpinnerSvg />
          <span>Discovering 2 more…</span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="h-3 w-20 rounded bg-neutral-200" />
          <div className="flex gap-1.5">
            <span className="h-5 w-12 rounded-md bg-neutral-100" />
            <span className="h-5 w-5 rounded-md bg-neutral-900" />
          </div>
        </div>
        <div className="mt-8 space-y-3">
          <div className="h-6 w-3/4 rounded bg-neutral-300" />
          <div className="h-6 w-1/2 rounded bg-neutral-300" />
        </div>
        <div className="mt-6 space-y-1.5">
          <div className="h-2 w-full rounded bg-neutral-200" />
          <div className="h-2 w-5/6 rounded bg-neutral-200" />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="h-16 rounded-md bg-neutral-100" />
          <div className="h-16 rounded-md bg-neutral-100" />
        </div>
      </div>
    </div>
  );
}

// Cascading visual #3 — share link → reviewer pins, no account required.
function VisualShare() {
  return (
    <div className="relative h-[340px] bg-neutral-50 sm:h-[400px]">
      <div className="absolute inset-0 flex flex-col gap-4 p-8 sm:p-10">
        <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 shadow-sm">
          <LinkIcon />
          <span className="flex-1 truncate font-mono text-xs text-neutral-500">
            clarity.avista.is/p/Qa8…7Rv
          </span>
          <span className="rounded-md bg-neutral-900 px-2.5 py-1 text-[10px] font-medium text-white">
            Copy
          </span>
        </div>
        <div className="flex items-center justify-center text-neutral-400">
          <ArrowDownIcon />
        </div>
        <div className="relative flex-1 overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="p-5">
            <div className="h-2 w-16 rounded bg-neutral-200" />
            <div className="mt-3 space-y-2">
              <div className="h-4 w-3/4 rounded bg-neutral-300" />
              <div className="h-4 w-1/2 rounded bg-neutral-300" />
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="h-2 w-full rounded bg-neutral-200" />
              <div className="h-2 w-5/6 rounded bg-neutral-200" />
            </div>
          </div>
          <div className="absolute left-[20%] top-[40%]">
            <PinShape label="JP" tone="open" />
          </div>
          <div className="absolute right-[18%] top-[28%]">
            <PinShape label="HE" tone="open" />
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
            <span className="text-[10px] text-neutral-500">No signup needed</span>
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <CheckSvg />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Product showcase (tabbed) ──────────────────────────────────────────────
//
// One large card. The tab strip swaps the inner visual so a visitor can
// browse facets of the tool without scrolling through five separate
// sections. Smaller features that didn't earn a cascading block live here.

type ShowcaseTab = {
  id: string;
  label: string;
  title: string;
  body: string;
  visual: () => React.ReactNode;
};

const SHOWCASE_TABS: ShowcaseTab[] = [
  {
    id: "viewport",
    label: "Viewport previews",
    title: "Review every breakpoint without switching devices.",
    body: "Toggle Desktop, Tablet, and Mobile widths in the toolbar. The proxied page reflows in place — pins stay anchored to elements, not pixels, so a comment on the mobile nav still points at it on desktop.",
    visual: () => <ShowcaseViewport />,
  },
  {
    id: "threads",
    label: "Threaded replies",
    title: "Conversations stay attached to the pin.",
    body: "Replies hang under the original comment. Resolve when the work ships, reopen if it regresses. The pin's device icon tells you which viewport the reviewer was on when they posted.",
    visual: () => <ShowcaseThreads />,
  },
  {
    id: "notify",
    label: "Email notifications",
    title: "The right people get pinged automatically.",
    body: "New comments fan out to the project creator and the thread's existing participants. No mailing list, no @-mention syntax — just normal threading semantics.",
    visual: () => <ShowcaseNotify />,
  },
  {
    id: "visibility",
    label: "Hide pages",
    title: "Trim what collaborators see.",
    body: "Some pages are works-in-progress. Click the eye next to any page to hide it from the collaborator view; reveal again when it's ready. Comments on hidden pages are preserved for when it comes back.",
    visual: () => <ShowcaseVisibility />,
  },
];

function ProductShowcase() {
  return (
    <section className="border-t border-neutral-200 bg-neutral-50/60">
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-32">
        <SectionEyebrow>Inside the tool</SectionEyebrow>
        <h2 className="mt-4 max-w-3xl text-3xl font-semibold leading-tight tracking-tight text-neutral-900 md:text-5xl">
          Built around the small details.
        </h2>
        <p className="mt-5 max-w-2xl text-lg text-neutral-600">
          Click through to see the bits that quietly make Clarity feel right.
        </p>

        <ShowcaseTabs />
      </div>
    </section>
  );
}

function ShowcaseTabs() {
  const [activeId, setActiveId] = useState<string>(SHOWCASE_TABS[0]?.id ?? "");
  const active = SHOWCASE_TABS.find((t) => t.id === activeId) ?? SHOWCASE_TABS[0];
  if (!active) return null;

  return (
    <div className="mt-12 overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,15,15,0.15),0_8px_30px_-12px_rgba(15,15,15,0.08)]">
      <div className="flex flex-wrap gap-1 border-b border-neutral-200 bg-neutral-50/70 p-2">
        {SHOWCASE_TABS.map((tab) => {
          const isActive = tab.id === active.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveId(tab.id)}
              className={
                "rounded-full px-4 py-2 text-xs font-medium transition " +
                (isActive
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900")
              }
              aria-pressed={isActive}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="grid items-center gap-8 p-8 md:grid-cols-[1fr,1.1fr] md:gap-12 md:p-12">
        {/* `key={active.id}` forces remount on tab switch, which retriggers
            the keyframe and gives the prose + visual a soft cross-fade. */}
        <div key={`${active.id}-prose`} className="clarity-fade-in">
          <h3 className="text-2xl font-semibold leading-tight tracking-tight text-neutral-900 md:text-3xl">
            {active.title}
          </h3>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-neutral-600">
            {active.body}
          </p>
        </div>
        <div
          key={`${active.id}-visual`}
          className="clarity-fade-in rounded-2xl border border-neutral-200 bg-neutral-50/60 p-6"
        >
          {active.visual()}
        </div>
      </div>
    </div>
  );
}

function ShowcaseViewport() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center gap-1 rounded-full border border-neutral-200 bg-white p-1">
        <span className="rounded-full bg-neutral-900 px-3 py-1 text-[10px] font-semibold text-white">
          Desktop
        </span>
        <span className="px-3 py-1 text-[10px] font-semibold text-neutral-500">
          Tablet
        </span>
        <span className="px-3 py-1 text-[10px] font-semibold text-neutral-500">
          Mobile
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md border border-neutral-200 bg-white p-2">
          <div className="h-1.5 w-8 rounded bg-neutral-200" />
          <div className="mt-1.5 h-2 w-full rounded bg-neutral-300" />
          <div className="mt-3 h-12 rounded bg-neutral-100" />
        </div>
        <div className="rounded-md border-2 border-neutral-900 bg-white p-2">
          <div className="h-1.5 w-8 rounded bg-neutral-200" />
          <div className="mt-1.5 h-2 w-full rounded bg-neutral-300" />
          <div className="mt-3 h-12 rounded bg-neutral-100" />
        </div>
        <div className="rounded-md border border-neutral-200 bg-white p-2">
          <div className="h-1.5 w-8 rounded bg-neutral-200" />
          <div className="mt-1.5 h-2 w-full rounded bg-neutral-300" />
          <div className="mt-3 h-12 rounded bg-neutral-100" />
        </div>
      </div>
    </div>
  );
}

function ShowcaseThreads() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-neutral-200 bg-white p-3">
        <div className="flex items-start gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-semibold text-white">
            DH
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium text-neutral-900">
              David H. <span className="font-normal text-neutral-400">· 14m</span>
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-neutral-700">
              Headline could be one line tighter on mobile.
            </p>
          </div>
          <DeviceMobileSvg />
        </div>
      </div>
      <div className="ml-6 rounded-xl border border-neutral-200 bg-white p-3">
        <div className="flex items-start gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold" style={{ background: "#1AE07D", color: "#0a2a17" }}>
            HE
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium text-neutral-900">
              Helena E. <span className="font-normal text-neutral-400">· 6m</span>
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-neutral-700">
              Reduced to two lines. Take a look?
            </p>
          </div>
          <DeviceDesktopSvg />
        </div>
      </div>
      <div className="flex items-center gap-2 px-3">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
          Resolved
        </span>
        <span className="text-[10px] text-neutral-500">by David H.</span>
      </div>
    </div>
  );
}

function ShowcaseNotify() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center gap-2 border-b border-neutral-200 px-3 py-2">
        <div className="flex gap-1">
          <span className="h-2 w-2 rounded-full bg-neutral-300" />
          <span className="h-2 w-2 rounded-full bg-neutral-300" />
          <span className="h-2 w-2 rounded-full bg-neutral-300" />
        </div>
        <span className="ml-2 text-[10px] font-mono text-neutral-400">Inbox</span>
      </div>
      <div className="divide-y divide-neutral-100">
        <div className="px-3 py-2">
          <p className="text-[11px] font-medium text-neutral-900">
            Helena E. replied on Homepage hero
          </p>
          <p className="mt-0.5 text-[10px] text-neutral-500">
            “Reduced to two lines. Take a look?”
          </p>
        </div>
        <div className="px-3 py-2">
          <p className="text-[11px] font-medium text-neutral-900">
            Jón P. commented on LHI-Design v2
          </p>
          <p className="mt-0.5 text-[10px] text-neutral-500">
            “CTA copy — let’s try a question.”
          </p>
        </div>
        <div className="px-3 py-2">
          <p className="text-[11px] font-medium text-neutral-900">
            David H. resolved a thread on LHI-Design v2
          </p>
        </div>
      </div>
    </div>
  );
}

function ShowcaseVisibility() {
  return (
    <div className="space-y-2 rounded-xl border border-neutral-200 bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
        Pages
      </p>
      <ul className="space-y-1 text-xs">
        <li className="group flex items-center justify-between rounded-md px-2 py-1.5 text-neutral-700 hover:bg-neutral-100">
          <span>Home</span>
          <EyeSvg />
        </li>
        <li className="group flex items-center justify-between rounded-md px-2 py-1.5 text-neutral-700 hover:bg-neutral-100">
          <span>About</span>
          <EyeSvg />
        </li>
        <li className="flex items-center justify-between rounded-md px-2 py-1.5 italic text-neutral-400">
          <span>Pricing — work in progress</span>
          <EyeOffSvg />
        </li>
        <li className="group flex items-center justify-between rounded-md px-2 py-1.5 text-neutral-700 hover:bg-neutral-100">
          <span>Contact</span>
          <EyeSvg />
        </li>
      </ul>
      <p className="pt-1 text-[10px] text-neutral-500">
        Hidden pages stay queryable for owners. Collaborators never see them in the sidebar.
      </p>
    </div>
  );
}

// ── Tiny atom icons for the showcase / visuals ─────────────────────────────

function EyeSvg() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-neutral-400">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffSvg() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-neutral-400">
      <path d="M3 3l18 18" />
      <path d="M10.6 6.1A10.5 10.5 0 0112 6c6.5 0 10 7 10 7a17.7 17.7 0 01-3.7 4.4M6.6 6.6A17.7 17.7 0 002 12s3.5 7 10 7c1.5 0 2.8-.3 4-.8" />
      <path d="M9.9 9.9a3 3 0 004.2 4.2" />
    </svg>
  );
}

function CheckSvg() {
  return (
    <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8.5l3 3 7-7" />
    </svg>
  );
}

function SpinnerSvg() {
  return (
    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" className="animate-spin text-neutral-400">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function DeviceMobileSvg() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-neutral-400">
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <path d="M11 18.5h2" strokeLinecap="round" />
    </svg>
  );
}

function DeviceDesktopSvg() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-neutral-400">
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" strokeLinecap="round" />
    </svg>
  );
}

// ── Built for Avista clients ───────────────────────────────────────────────

function BuiltForAvista() {
  return (
    <section className="border-t border-neutral-200 bg-neutral-900 text-white">
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-28">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-400">
              Built for Avista clients
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
              Made in-house at Avista. Used with our clients.
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-300">
              Clarity is the tool we wanted for our own design and development
              work — point-and-click feedback on live sites without the
              account-creation tax. Now it&apos;s available to every client we
              work with.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/login"
                className="rounded-md bg-white px-5 py-2.5 text-sm font-medium text-neutral-900 transition hover:bg-neutral-200"
              >
                Log in
              </Link>
              <a
                href="mailto:david@avista.is?subject=Clarity%20access"
                className="rounded-md border border-neutral-700 px-5 py-2.5 text-sm font-medium text-neutral-200 transition hover:bg-neutral-800"
              >
                Talk to your Avista contact
              </a>
            </div>
          </div>

          {/* Small "card stack" graphic */}
          <div className="relative hidden h-72 md:block">
            <div className="absolute inset-x-12 top-6 h-56 rotate-[-3deg] rounded-2xl border border-neutral-700 bg-neutral-800 shadow-2xl" />
            <div className="absolute inset-x-8 top-2 h-56 rotate-[2deg] rounded-2xl border border-neutral-700 bg-neutral-800 shadow-2xl" />
            <div className="absolute inset-x-4 top-0 h-56 rounded-2xl border border-neutral-700 bg-neutral-800/90 p-6 shadow-2xl backdrop-blur">
              <p className="text-[10px] font-medium uppercase tracking-wider text-neutral-500">
                Project · LHI-Design v2
              </p>
              <p className="mt-4 text-2xl font-semibold leading-tight">
                Homepage hero.
              </p>
              <div className="mt-4 space-y-2">
                <MiniRow initials="DH" body="Logo should sit on the left." />
                <MiniRow initials="HE" body="Photo can crop tighter." brand />
                <MiniRow initials="JP" body="CTA copy ✓." muted />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniRow({
  initials,
  body,
  brand = false,
  muted = false,
}: {
  initials: string;
  body: string;
  brand?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      <span
        className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-semibold"
        style={
          brand
            ? { background: "#1AE07D", color: "#0a2a17" }
            : { background: "#262626", color: "#f5f5f5" }
        }
      >
        {initials}
      </span>
      <p className={`text-xs leading-snug ${muted ? "text-neutral-500 line-through" : "text-neutral-200"}`}>
        {body}
      </p>
    </div>
  );
}

// ── Closing CTA ────────────────────────────────────────────────────────────

function ClosingCta({
  primaryHref,
  primaryLabel,
}: {
  primaryHref: string;
  primaryLabel: string;
}) {
  return (
    <section className="border-t border-neutral-200 bg-white">
      <div className="mx-auto max-w-6xl px-6 py-24 text-center md:py-28">
        <h2 className="text-3xl font-semibold tracking-tight text-neutral-900 md:text-5xl">
          Ready to start a review?
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-neutral-600">
          Log in to your Clarity account and create your first project.
        </p>
        <div className="mt-8 flex justify-center">
          <Link
            to={primaryHref}
            className="rounded-md bg-neutral-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-neutral-700"
          >
            {primaryLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Footer ─────────────────────────────────────────────────────────────────

function SiteFooter() {
  return (
    <footer className="border-t border-neutral-200 bg-neutral-50">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/clarity_logo.svg"
              alt="Clarity by Avista"
              className="h-6 w-auto"
            />
            <span className="text-xs text-neutral-500">
              Point-and-click feedback on any website.
            </span>
          </div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-600">
            <a href="#how-it-works" className="hover:text-neutral-900">
              How it works
            </a>
            <a
              href="mailto:david@avista.is?subject=Clarity"
              className="hover:text-neutral-900"
            >
              Contact
            </a>
            <a
              href="https://avista.is"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-neutral-900"
            >
              avista.is
            </a>
          </nav>
        </div>
        <div className="mt-8 flex flex-col gap-2 border-t border-neutral-200 pt-6 text-xs text-neutral-500 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Avista. All rights reserved.</p>
          <p>
            Made by{" "}
            <a
              href="https://avista.is"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-neutral-700 hover:text-neutral-900"
            >
              Avista
            </a>
            .
          </p>
        </div>
      </div>
    </footer>
  );
}

// ── Shared atoms ───────────────────────────────────────────────────────────

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
      {children}
    </p>
  );
}

function Pin({
  x,
  y,
  label,
  tone = "open",
  live = false,
}: {
  x: string;
  y: string;
  label: string;
  tone?: "open" | "resolved";
  // When true, wraps the pin in the live-pulse ring so it reads as
  // "this comment just landed" in the hero mock.
  live?: boolean;
}) {
  return (
    <div
      aria-hidden
      className="absolute"
      style={{ top: y, left: x, transform: "translate(-50%, -100%)" }}
    >
      <span
        className={
          "inline-block rounded-full " +
          (live ? "clarity-live-pulse" : "")
        }
      >
        <PinShape label={label} tone={tone} />
      </span>
    </div>
  );
}

function PinShape({
  label,
  tone,
}: {
  label: string;
  tone: "open" | "resolved";
}) {
  const bg = tone === "resolved" ? "#E05505" : "#1AE07D";
  const fg = tone === "resolved" ? "#ffffff" : "#0a2a17";
  return (
    <div
      className="inline-flex h-7 min-w-[28px] items-center justify-center border-2 border-white px-1.5 text-[11px] font-semibold leading-none shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
      style={{
        background: bg,
        color: fg,
        borderRadius: "9999px 9999px 9999px 2px",
      }}
    >
      {label}
    </div>
  );
}

function FloatingComment({ x, y }: { x: string; y: string }) {
  return (
    <div
      className="absolute z-10 w-[230px] rounded-xl border border-neutral-200 bg-white p-3 text-left shadow-[0_18px_44px_-12px_rgba(15,15,15,0.18)]"
      style={{ top: y, left: x }}
      aria-hidden
    >
      <div className="flex items-start gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-semibold text-white">
          HE
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-neutral-900">
            Helena E. <span className="font-normal text-neutral-400">· 2m</span>
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-neutral-700">
            Love the new CTA copy. Maybe try a green hover state?
          </p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="flex-1 rounded-md border border-neutral-200 px-2 py-1 text-[10px] text-neutral-400">
          Reply…
        </span>
        <span className="rounded-md bg-neutral-900 px-2 py-1 text-[10px] text-white">
          Send
        </span>
      </div>
    </div>
  );
}

function MockComment({
  initials,
  name,
  body,
}: {
  initials: string;
  name: string;
  body: string;
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-[9px] font-semibold text-white">
          {initials}
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-neutral-900">
            {name}
            <span className="font-normal text-neutral-400"> · just now</span>
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-neutral-700">{body}</p>
        </div>
      </div>
    </div>
  );
}

function Avatar({ mark, tone }: { mark: string; tone?: "brand" }) {
  const style =
    tone === "brand"
      ? { background: "#1AE07D", color: "#0a2a17" }
      : { background: "#0a0a0a", color: "#ffffff" };
  return (
    <span
      className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[9px] font-semibold shadow-sm"
      style={style}
    >
      {mark}
    </span>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-neutral-400">
      <rect x="3" y="7" width="10" height="6" rx="1.5" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-neutral-500">
      <path d="M7 9a3 3 0 0 0 4 0l2-2a3 3 0 0 0-4-4L8 4" />
      <path d="M9 7a3 3 0 0 0-4 0L3 9a3 3 0 0 0 4 4l1-1" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M6 2v8M3 7l3 3 3-3" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7Z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function IconLayers() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 2 8l10 5 10-5-10-5Z" />
      <path d="m2 16 10 5 10-5" />
      <path d="m2 12 10 5 10-5" />
    </svg>
  );
}

function IconViewport() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="12" height="9" rx="1.5" />
      <path d="M7 18h6M10 14v4" />
      <rect x="16" y="9" width="5" height="10" rx="1" />
    </svg>
  );
}

function IconThread() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}
